import { ErrorLogComponent } from '@cian/error-log-component';
import { UiKitRoot } from '@cian/ui-kit';
import { useConstant } from '@cian/react-utils';

import { type FC, useMemo } from 'react';

import { EventEmitter, type IEventEmitter } from 'shared/services/eventEmitter';
import { AdBannerWithPostViewAnalytics } from 'shared/components/AdBannerWithPostViewAnalytics';
import { AdfoxBannerContextProvider } from 'shared/components/AdfoxBannerContext';
import { ApplicationContext } from 'shared/utils/applicationContext';
import { BannerCarouselContainer } from 'shared/components/BannerCarousel';
import { type IApplicationContext } from 'shared/types/applicationContext';
import { type InitialAdfoxAnalyticsPayload } from 'shared/types/analytics';
import { subscribeBannerLifecycleReporting } from 'shared/utils/subscribeBannerLifecycleReporting';
import { type TAdBannerMicrofrontendParameters } from 'shared/types/microfrontendParameters';
import { type TBannerEvents } from 'shared/types/events';

type TApplicationProps = {
  context: IApplicationContext;
  parameters: TAdBannerMicrofrontendParameters;
  instanceId: string;
  dispatchLifecycleEvent(event: TBannerEvents): void;
};

export const Application: FC<TApplicationProps> = ({ context, parameters, instanceId, dispatchLifecycleEvent }) => {
  const {
    logger,
    custom: { initialDeviceType },
  } = context;

  const {
    place,
    puids,
    sizes,
    pParams,
    regionId,
    position,
    galleryPosition,
    isSp,
    tgbNativedescGeneralDesign,
    carousel,
    deviceType,
  } = parameters;

  const placeId = parameters.placeId ?? instanceId;

  // Колбэки аналитики не пересекают границу микрофронтенда (см. IAdBannerMicrofrontendParameters),
  // поэтому собираем сериализуемую часть payload здесь. useMemo держит ссылку стабильной,
  // иначе новый объект на каждый рендер инвалидирует useMemo контекста и ре-рендерит всех потребителей.
  const analyticsData = useMemo<InitialAdfoxAnalyticsPayload>(
    () => ({
      galleryPosition,
      isSp,
      place,
      position,
    }),
    [galleryPosition, isSp, place, position],
  );

  // Внутренний эмиттер баннера. В режиме одиночного баннера lifecycle-репортинг уходит по подписке.
  // В режиме карусели подписка не создаётся — BannerCarouselContainer управляет своими эмиттерами сам.
  const eventEmitter = useConstant<IEventEmitter>(() => {
    const emitter = new EventEmitter();

    if (!carousel) {
      subscribeBannerLifecycleReporting({ dispatchLifecycleEvent, eventEmitter: emitter, placeId });
    }

    return emitter;
  });

  return (
    <ErrorLogComponent logger={logger}>
      <ApplicationContext.Provider value={context}>
        <UiKitRoot deviceType={deviceType || initialDeviceType} useStaticDeviceType={!!deviceType}>
          {carousel ? (
            <BannerCarouselContainer
              parameters={parameters}
              placeId={placeId}
              dispatchLifecycleEvent={dispatchLifecycleEvent}
            />
          ) : (
            <AdfoxBannerContextProvider
              eventEmitter={eventEmitter}
              puids={puids}
              sizes={sizes}
              pParams={pParams}
              regionId={regionId}
              analyticsData={analyticsData}
              tgbNativedescGeneralDesign={tgbNativedescGeneralDesign}
              placeId={placeId}
            >
              <AdBannerWithPostViewAnalytics />
            </AdfoxBannerContextProvider>
          )}
        </UiKitRoot>
      </ApplicationContext.Provider>
    </ErrorLogComponent>
  );
};
